import { adminMutation } from "@/admin/utils/adminMutation.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { queryOptions, type QueryKey } from "@tanstack/react-query";
import type { Campaigns, CampaignsCreateBody, CampaignsUpdateBody } from "@warrenio/api-spec/spec.oats.gen";

//#region Constants
export const campaignTypeMap = { new_users: "New Users", all_users: "All Users" };
//#endregion

//#region Response
type Response = Campaigns[];
//#endregion

//#region Query and parameters
const queryKey: QueryKey = ["campaigns"];

function getQuery(client: ApiClient) {
    return queryOptions({
        queryKey,
        queryFn: async ({ signal }): Promise<Response> => {
            return getResponseData(await client.GET("/payment/admin/campaign/list", { signal }));
        },
    });
}
//#endregion

//#region Atoms
export const campaignsQueryAtom = atomFromStandardQueryOptions(getQuery);
//#endregion

//#region Mutations

const entity = "Campaign";

export const campaignCreateMutation = adminMutation((apiClient) => ({
    entity,
    operation: "create",
    requestFn: async ({ body }: { body: CampaignsCreateBody }) =>
        await apiClient.POST("/payment/admin/campaign", {
            ...jsonEncodedBody,
            body,
        }),
    invalidateQueryKey: queryKey,
}));

export const campaignUpdateMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ id, body }: { id: Campaigns["id"]; body: CampaignsUpdateBody }) =>
        await apiClient.PUT("/payment/admin/campaign/{id}", {
            params: { path: { id: String(id) } },
            ...jsonEncodedBody,
            body,
        }),
    invalidateQueryKey: queryKey,
}));

export const campaignDeleteMutation = adminMutation((apiClient) => ({
    entity,
    operation: "delete",
    requestFn: async ({ id }: { id: Campaigns["id"] }) =>
        await apiClient.DELETE("/payment/admin/campaign/{id}", {
            params: { path: { id: String(id) } },
        }),
    invalidateQueryKey: queryKey,
}));

//#endregion Mutations
